import { useState, useEffect, useCallback, useRef, forwardRef } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, Eye, EyeOff, Plus, Trash2, Upload, X } from 'lucide-react'
import { cn } from '@/shared/utils'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { useAccountRestrictions } from '@/shared/accessControl'
import { useAuthStore } from '@/store/useAuthStore'
import { userService } from '@/api/services/userService'
import { schoolService } from '@/api/services/schoolService'
import { useUserTypeEnum, useGenderEnum, useGearEnum } from '@/api/hooks/useUsers'
import { useQuery } from '@tanstack/react-query'
import { useSchoolAdminRolesV2, useMapSchoolAdminRoleUsersV2 } from '@/api/hooks'
import { toArray } from '@/features/roles/rolesUtils'
import { endpoints } from '@/api/endpoints'
import { restClient } from '@/api/client'
import { getApiError } from '@/api/client'
import { AddressPicker, EMPTY_ADDRESS, ADDRESS_DEFAULT_CENTER, type AddressValue } from './details/AddressPicker'

// ─── Field helpers ────────────────────────────────────────────────────────────

function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  )
}

const Input = forwardRef<HTMLInputElement, any>(function Input({ className = '', error, ...props }, ref) {
  return (
    <input
      ref={ref}
      {...props}
      className={cn(
        'w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400',
        error ? 'border-red-400 bg-red-50' : 'border-gray-300',
        props.disabled && 'bg-gray-100 text-gray-500 cursor-not-allowed',
        className
      )}
    />
  )
})

const Select = forwardRef<HTMLSelectElement, any>(function Select({ className = '', error, children, ...props }, ref) {
  return (
    <select
      ref={ref}
      {...props}
      className={cn(
        'w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white',
        error ? 'border-red-400 bg-red-50' : 'border-gray-300',
        props.disabled && 'bg-gray-100 text-gray-500 cursor-not-allowed',
        className
      )}
    >
      {children}
    </select>
  )
})

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null
  return <p className="mt-1 text-xs text-red-500">{msg}</p>
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="text-sm font-bold text-gray-800">{title}</h3>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  )
}

// get-list returns areas as `translations: [{ locale: 'En', name }]`; older/other
// endpoints used `state_translations` / `state_province_translations`. Accept all
// three so the dropdown never falls back to "Unknown".
function areaName(a: any): string | undefined {
  const lists = [a?.translations, a?.state_translations, a?.state_province_translations]
  for (const list of lists) {
    if (!Array.isArray(list) || !list.length) continue
    const en = list.find((tr: any) => tr?.locale?.toLowerCase() === 'en')
    const name = en?.name ?? list[0]?.name
    if (name) return name
  }
  return a?.name ?? undefined
}

// The saved value and the enum option can differ in case ("Both" vs "BOTH"), and a
// <select> only keeps a value that matches an <option> exactly — so resolve the
// stored value back to the enum's own spelling before prefilling.
function matchEnum(options: any[], value: any): string {
  if (value === null || value === undefined || value === '') return ''
  const v = String(value).trim().toLowerCase()
  const hit = options.find((o: any) =>
    String(o?.value ?? '').trim().toLowerCase() === v ||
    String(o?.name  ?? '').trim().toLowerCase() === v)
  return hit ? String(hit.value) : String(value)
}

// ─── USER TYPE TABS ───────────────────────────────────────────────────────────
const USER_TYPES = [
  { value: 'STUDENT',     labelKey: 'userCreate.userTypeStudent' },
  { value: 'TRAINER',     labelKey: 'userCreate.userTypeTrainer' },
  { value: 'ASSISTANT',   labelKey: 'userCreate.userTypeAssistant' },
  { value: 'SCHOOL_ADMIN',labelKey: 'userCreate.userTypeSchoolAdmin' },
  { value: 'ADMIN',       labelKey: 'userCreate.userTypeAdmin' },
]

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export function UserCreatePage() {
  const { t }     = useTranslation()
  const confirm   = useConfirm()
  const navigate  = useNavigate()
  const location  = useLocation()
  const inSchool   = location.pathname.startsWith('/school')
  const { id }    = useParams()
  const isEdit     = !!id
  const { user }  = useAuthStore()
  const userType  = (user as any)?.user_type ?? (user as any)?.user?.user_type

  const isSchoolAdmin = String(userType ?? '').toUpperCase() === 'SCHOOL_ADMIN'

  // Restricted accounts (e.g. sharjah@gov.ae) can't create admin users.
  const { canCreateAdmin } = useAccountRestrictions()
  const cannotCreateAdmin = !canCreateAdmin

  // The logged-in school admin's own school. New trainers must default to this
  // school without the admin choosing one, so we fetch it from the backend
  // (auth/vendor/get-vendor) rather than relying on the auth payload shape.
  const { data: myVendorData } = useQuery({
    queryKey: ['my-vendor', 'user-create'],
    queryFn: () => schoolService.myVendor(),
    enabled: isSchoolAdmin,
    staleTime: 5 * 60_000,
  })
  // myVendor arrives in several shapes: paginator, bare array, or single object,
  // with the vendor nested under row.school / row.vendor or at the root.
  const vendorBody: any = (myVendorData as any)?.data ?? myVendorData ?? {}
  const vendorRow: any = Array.isArray(vendorBody)
    ? (vendorBody[0] ?? {})
    : Array.isArray(vendorBody?.data)
      ? (vendorBody.data[0] ?? {})
      : vendorBody
  const vendorObj: any = vendorRow.school ?? vendorRow.vendor ?? vendorRow

  // Resolve the vendor id, preferring the fetched vendor and falling back to
  // every shape the auth payload might expose (school_id maps to vendor_id).
  const mySchoolId =
    vendorRow.vendor_id
    ?? vendorRow.vendor?.id
    ?? vendorObj?.id
    ?? vendorRow.id
    ?? (user as any)?.school_id
    ?? (user as any)?.school?.id
    ?? (user as any)?.user?.school_id
    ?? (user as any)?.user?.school?.id
  const mySchoolName =
    vendorObj?.vendor_translations?.find((vt: any) => vt.locale?.toLowerCase() === 'en')?.name
    ?? vendorObj?.vendor_translations?.[0]?.name
    ?? (user as any)?.school?.vendor_translations?.find((vt: any) => vt.locale?.toLowerCase() === 'en')?.name
    ?? (user as any)?.school?.vendor_translations?.[0]?.name
    ?? (user as any)?.user?.school?.vendor_translations?.[0]?.name
    ?? (user as any)?.school?.name
    ?? (user as any)?.user?.school?.name
    ?? t('userCreate.yourSchool')

  const [showPassword,    setShowPassword]    = useState(false)
  const [submitting,      setSubmitting]      = useState(false)
  const [successMsg,      setSuccessMsg]      = useState('')
  const [errorMsg,        setErrorMsg]        = useState('')
  const [photoPreview,    setPhotoPreview]    = useState<string | null>(null)
  const [photoFile,       setPhotoFile]       = useState<File | null>(null)
  const [schoolSearch,    setSchoolSearch]    = useState('')
  const [assistantAccess, setAssistantAccess] = useState<any[]>([])
  // Optional address captured inline on the create form. The trainer is created
  // first (no user id exists yet), then this address is saved against the new id.
  // A new trainer's first address is always their default — no need to ask.
  const [addressForm, setAddressForm] = useState<AddressValue>({ ...EMPTY_ADDRESS, is_default: true })
  const [addressPos,  setAddressPos]  = useState(ADDRESS_DEFAULT_CENTER)

  // ── Enums ─────────────────────────────────────────────────────────────────
  const { data: enumData }   = useUserTypeEnum()
  const { data: genderData } = useGenderEnum()
  const { data: gearData }   = useGearEnum()

  const enumValues: any[] = enumData?.values ?? []
  const genders:    any[] = genderData?.values ?? []
  const gears:      any[] = gearData?.values ?? []

  // ── Dialects / languages ──────────────────────────────────────────────────
  const { data: dialectsData } = useQuery({
    queryKey: ['dialects'],
    queryFn: () => restClient.get(`${endpoints.users.listDialect}?page=1&limit=1000`).then(r => r.data),
    staleTime: Infinity,
  })
  const dialects: any[] = dialectsData?.data ?? dialectsData?.dialects ?? []

  // ── Cities ────────────────────────────────────────────────────────────────
  // Admin city list (not the public one, which drops unpublished cities).
  const { data: citiesData } = useQuery({
    queryKey: ['cities'],
    queryFn: () => restClient.get(`${endpoints.city.list}?limit=1000&locale=en`).then(r => r.data),
    staleTime: Infinity,
  })
  const cities: any[] = citiesData?.data ?? citiesData?.cities ?? []

  // ── Categories (vehicle types) ────────────────────────────────────────────
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => restClient.get(`${endpoints.users.listCategory}?page=1&limit=1000`).then(r => r.data),
    staleTime: Infinity,
  })
  const categories: any[] = categoriesData?.data ?? []

  // ── Schools ───────────────────────────────────────────────────────────────
  const { data: schoolsData } = useQuery({
    queryKey: ['schools', schoolSearch],
    queryFn: () => schoolService.list({ limit: 1000, search: schoolSearch }),
    staleTime: 30_000,
  })
  const schools: any[] = schoolsData?.data ?? []

  // ── Roles ─────────────────────────────────────────────────────────────────
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => restClient.get(`${endpoints.rolesAndPermission.getRoles}?page=1&limit=1000`).then(r => r.data),
    staleTime: Infinity,
  })
  const roles: any[] = rolesData?.data ?? []

  // School roles a school admin can give the new trainer (applied right after creation).
  const schoolRolesQ = useSchoolAdminRolesV2()
  const mapSchoolRoleUsers = useMapSchoolAdminRoleUsersV2()
  const schoolRoles: { id: number; name: string; description?: string }[] =
    isSchoolAdmin && !isEdit ? toArray(schoolRolesQ.data, 'roles') : []
  const [schoolRoleIds, setSchoolRoleIds] = useState<number[]>([])

  // ── Assistant access enum ─────────────────────────────────────────────────
  useEffect(() => {
    // Same base URL as every other call (VITE_HOST_API in .env), not a hardcoded host.
    restClient.get(endpoints.enum.assistantAccess)
      .then(r => setAssistantAccess(r.data?.values ?? []))
      .catch(() => setAssistantAccess([]))
  }, [])

  // ── Form ──────────────────────────────────────────────────────────────────
  const { register, control, watch, handleSubmit, setValue, formState: { errors, dirtyFields } } = useForm({
    defaultValues: {
      user_type:         isSchoolAdmin ? 'TRAINER' : 'STUDENT',
      name:              '',
      name_ar:           '',
      name_ur:           '',
      email:             '',
      password:          '',
      phone:             '',
      dob:               '',
      city_id:           '',
      area_id:           '',
      gear:              '',
      gender:            '',
      user_gender:       '',
      vehicle_type_id:   '',
      vendor_id:         '',
      school_name:       '',
      is_active:         true,
      is_pickup_enabled: true,
      max_radius_in_km:  '',
      cash_clearance_date: '',
      certificate_expiry_date: '',
      vendor_commission_in_percentage: '',
      certificate_commission_in_percentage: '',
      cash_in_hand:                    '',
      max_cash_in_hand_allowed:        '',
      bio: '', bio_ar: '', bio_ur: '',
      vehicle_number:    '',
      languages:         [] as { dialect_id: string; fluency_level: string }[],
      city_assigned:     [] as string[],
      school_ids:        [] as string[],
      roles:             [] as string[],
      assistant_access:  '',
      assistant_id_front: null as File | null,
      assistant_id_back:  null as File | null,
      assistant_id_expiry: '',
      license_front:     null as File | null,
      license_back:      null as File | null,
    },
  })

  const watchedType      = watch('user_type')
  const watchedVendorId  = watch('vendor_id')
  const watchedPickup    = watch('is_pickup_enabled')
  const watchedCity      = watch('city_id')

  // ── Areas / states (cascade from selected city) ───────────────────────────
  const { data: areasData } = useQuery({
    queryKey: ['areas', watchedCity],
    queryFn: () => restClient.get(`${endpoints.users.listAreas}?page=1&limit=1000${watchedCity ? `&city_id=${watchedCity}` : ''}`).then(r => r.data),
    staleTime: 60_000,
  })
  const areasRaw: any[] = areasData?.data ?? areasData?.states ?? []
  // Each row carries its own `city`, so filter here too — the endpoint doesn't
  // always honour the city_id query param.
  const areas: any[] = watchedCity
    ? areasRaw.filter(a => {
        const cid = a?.city_id ?? a?.city?.id
        return cid == null || String(cid) === String(watchedCity)
      })
    : areasRaw

  // ── Edit mode: load the existing user and prefill the form ────────────────
  const { data: existingUser } = useQuery({
    queryKey: ['user-edit', id],
    queryFn: () => userService.getById(Number(id)),
    enabled: isEdit,
  })
  // Guards the one-time prefill below so it fires once per edited record —
  // without this, every re-render that changed `areas.length` (which happens
  // on every city the admin picks, since that refetches the area list) re-ran
  // the whole block and stomped the admin's in-progress city/area edits back
  // to whatever the server originally had.
  const prefilledForId = useRef<number | null>(null)
  // Area select can't hold a value before its <option>s exist, and those only
  // load *after* city_id is set below (which is what the areas query keys off
  // of) — so the area value gets re-applied once a matching option shows up,
  // by the separate effect right after this one.
  const pendingAreaId = useRef<string | null>(null)
  useEffect(() => {
    if (!isEdit || !existingUser) return
    if (prefilledForId.current === Number(id)) return
    // Wait for the city options so city_id's <select> can actually show it
    // selected — cities is always non-empty in practice, unlike some of the
    // other option lists below, so it's the one safe thing to hard-block on
    // (this now only gets the one shot, per the guard above).
    if (!cities.length) return
    const u = existingUser.data ?? existingUser.user ?? existingUser
    if (!u) return
    // get-user-by-id keeps identity fields at the root but nests every training
    // preference (city, area, gear, gender, category, pickup, bio, commissions)
    // under user_preference — read both so nothing comes back blank.
    const pref = u.user_preference ?? {}
    // Dates arrive as "YYYY-MM-DD HH:mm:ss"; <input type="date"> needs just the day.
    const day = (v: any) => (v ? String(v).slice(0, 10) : '')
    // Numeric fields are 0/null when unset — keep 0 but render null/undefined as ''.
    const num = (v: any) => (v === null || v === undefined || v === '' ? '' : String(v))

    setValue('user_type', (u.user_type ?? 'STUDENT'))
    setValue('name', u.name ?? '')
    setValue('name_ar', u.name_ar ?? '')
    setValue('name_ur', u.name_ur ?? '')
    setValue('email', u.email ?? '')
    setValue('phone', u.phone ?? '')
    setValue('dob', day(u.dob))
    setValue('city_id', String(pref.city_id ?? u.city_id ?? pref.city?.id ?? u.city?.id ?? ''))
    { const areaId = String(pref.state_province_id ?? pref.area_id ?? u.area_id ?? u.state_province_id ?? u.area?.id ?? '')
      setValue('area_id', areaId)
      pendingAreaId.current = areaId || null }
    setValue('gear',   matchEnum(gears,   pref.gear   ?? u.gear))
    setValue('gender', matchEnum(genders, pref.gender ?? u.gender))
    setValue('vehicle_type_id', String(pref.vehicle_type_id ?? u.vehicle_type_id ?? pref.vehicle_type?.id ?? u.category_id ?? ''))
    setValue('vendor_id', String(u.vendor_id ?? u.vendor?.id ?? pref.vendor_id ?? ''))
    setValue('school_name', u.school_name ?? '')
    setValue('vehicle_number', pref.traffic_file_number ?? u.vehicle_number ?? '')
    setValue('bio',    pref.bio    ?? u.bio    ?? '')
    setValue('bio_ar', pref.bio_ar ?? u.bio_ar ?? '')
    setValue('bio_ur', pref.bio_ur ?? u.bio_ur ?? '')
    setValue('is_active', u.is_active ?? true)
    setValue('is_pickup_enabled', pref.is_pickup_enabled ?? u.is_pickup_enabled ?? false)
    setValue('max_radius_in_km', num(pref.max_radius_in_km ?? u.max_radius_in_km))
    setValue('vendor_commission_in_percentage', num(u.vendor_commission_in_percentage ?? pref.school_commission_in_percentage))
    setValue('certificate_commission_in_percentage', num(pref.certificate_commission_in_percentage ?? u.certificate_commission_in_percentage))
    setValue('certificate_expiry_date', day(u.certificate_expiry_date))
    setValue('cash_clearance_date', day(u.cash_clearance_date))
    setValue('cash_in_hand', num(u.cash_in_hand))
    setValue('max_cash_in_hand_allowed', num(u.max_cash_in_hand_allowed))
    // languages[] comes back as { dialect_id, fluency_level }; the checkbox grid
    // compares dialect_id as a string.
    setValue('languages', (u.languages ?? [])
      .filter((l: any) => l?.dialect_id != null)
      .map((l: any) => ({ dialect_id: String(l.dialect_id), fluency_level: l.fluency_level || 'NATIVE' })))
    setValue('city_assigned', (u.city_assigned ?? u.cities ?? [])
      .map((c: any) => String(c?.city_id ?? c?.id ?? c)).filter(Boolean))
    setValue('school_ids', (u.schools ?? u.school_ids ?? [])
      .map((s: any) => String(s?.vendor_id ?? s?.id ?? s)).filter(Boolean))
    setValue('assistant_access', u.assistant_access ?? '')
    setValue('user_gender', matchEnum(genders, u.user_gender ?? u.gender))
    setValue('roles', (u.roles ?? []).map((r: any) => String(r?.id ?? r)).filter(Boolean))
    if (u.photo_url) setPhotoPreview(typeof u.photo_url === 'string' ? u.photo_url : null)
    prefilledForId.current = Number(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingUser, isEdit, id, setValue,
      cities.length, categories.length, schools.length, dialects.length, roles.length,
      gears.length, genders.length])

  // Select fields can't show a value until their <option>s exist, and each list loads from its own
  // request — some after the one-shot prefill above has already run. Re-apply each select once, the
  // moment its options arrive, unless the admin has already touched that field.
  const reapplied = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!isEdit || !existingUser || prefilledForId.current !== Number(id)) return
    const u = existingUser.data ?? existingUser.user ?? existingUser
    if (!u) return
    const pref = u.user_preference ?? {}
    const apply = (field: any, ready: boolean, value: string) => {
      const key = `${id}:${field}`
      if (reapplied.current.has(key) || !ready || !value) return
      reapplied.current.add(key)
      if (!(dirtyFields as any)[field]) setValue(field, value)
    }
    apply('gear',            gears.length > 0,         matchEnum(gears,   pref.gear   ?? u.gear))
    apply('gender',          genders.length > 0,       matchEnum(genders, pref.gender ?? u.gender))
    apply('user_gender',     genders.length > 0,       matchEnum(genders, u.user_gender ?? u.gender))
    apply('vehicle_type_id', categories.length > 0,    String(pref.vehicle_type_id ?? u.vehicle_type_id ?? pref.vehicle_type?.id ?? u.category_id ?? ''))
    apply('vendor_id',       schools.length > 0,       String(u.vendor_id ?? u.vendor?.id ?? pref.vendor_id ?? ''))
    apply('assistant_access', assistantAccess.length > 0, String(u.assistant_access ?? ''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingUser, id, isEdit, gears.length, genders.length, categories.length, schools.length, assistantAccess.length])

  // The areas list only becomes correct for this record *after* city_id is set
  // above (its query keys off the now-selected city), so area_id is re-applied
  // here once a matching <option> actually exists — once, via the pending ref,
  // so it never overwrites an area the admin picks afterward.
  useEffect(() => {
    if (!pendingAreaId.current) return
    if (!areas.some((a: any) => String(a.id) === pendingAreaId.current)) return
    setValue('area_id', pendingAreaId.current)
    pendingAreaId.current = null
  }, [areas, setValue])

  const isTrainer   = watchedType === 'TRAINER'
  const isStudent   = watchedType === 'STUDENT'
  const isAssistant = watchedType === 'ASSISTANT'
  const isCollector = watchedType === 'COLLECTOR'
  const isAdmin     = watchedType === 'ADMIN'

  const showOtherSchool = !watchedVendorId || watchedVendorId === 'other'

  // ── Photo upload ──────────────────────────────────────────────────────────
  const handlePhotoDrop = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPhotoFile(file)
    setPhotoPreview(URL.createObjectURL(file))
  }, [])

  // ── Submit ────────────────────────────────────────────────────────────────
  const onSubmit = handleSubmit(async (data) => {
    // Belt-and-braces: the tab is hidden, but never let a blocked account submit
    // an ADMIN payload (e.g. by editing an existing admin).
    if (cannotCreateAdmin && String(watchedType ?? '').toUpperCase() === 'ADMIN') {
      setErrorMsg(t('userCreate.adminCreationNotAllowed', 'You are not allowed to create or edit admin users.'))
      return
    }
    // Updating an existing user needs an explicit confirmation.
    if (isEdit && !(await confirm({
      title: t('common.confirmSaveTitle'), description: t('common.confirmSaveDescription'),
      confirmLabel: t('common.saveChanges'), variant: 'primary',
    }))) return
    setSubmitting(true)
    setErrorMsg('')
    setSuccessMsg('')
    try {
      // Create (admin/auth/register) takes `photo_url` as a plain string —
      // sending a raw file there throws "unsupported field type for
      // multipart.FileHeader". So upload it first and send the resulting URL
      // instead. Edit goes through admin/user/user-update-by-id, which accepts
      // the file directly as multipart `photo_url`, so no pre-upload there.
      // School-admin trainer create/edit uses a separate endpoint that also
      // accepts the file directly, so it's left untouched.
      let uploadedPhotoUrl: string | null = null
      if (photoFile && !isEdit && !isSchoolAdmin && !isAssistant) {
        try {
          uploadedPhotoUrl = await userService.uploadPhoto(photoFile)
        } catch (e: any) {
          setErrorMsg(e?.response?.data?.message ?? e?.message ?? t('userCreate.photoUploadFailed', 'Photo upload failed'))
          setSubmitting(false)
          return
        }
      }

      const body = new FormData()
      body.append('name',       data.name)
      body.append('email',      data.email)
      body.append('user_type',  data.user_type)
      if (data.password)        body.append('password', data.password)
      if (data.phone)         { body.append('phone', data.phone); body.append('country_code', '971') }
      if (data.dob)             body.append('dob', data.dob)
      if (uploadedPhotoUrl)         body.append('photo_url', uploadedPhotoUrl)
      else if (photoFile && (isEdit || !isAssistant)) body.append('photo_url', photoFile)

      // Trainer-specific
      if (isTrainer) {
        if (data.name_ar)  body.append('name_ar', data.name_ar)
        if (data.name_ur)  body.append('name_ur', data.name_ur)
        if (data.bio)      body.append('bio', data.bio)
        if (data.bio_ar)   body.append('bio_ar', data.bio_ar)
        if (data.bio_ur)   body.append('bio_ur', data.bio_ur)
        if (data.gear != null) body.append('gear', String(data.gear))
        if (data.certificate_expiry_date) body.append('certificate_expiry_date', data.certificate_expiry_date)
        if (data.vendor_commission_in_percentage) body.append('vendor_commission_in_percentage', String(data.vendor_commission_in_percentage))
        if (data.certificate_commission_in_percentage) body.append('certificate_commission_in_percentage', String(data.certificate_commission_in_percentage))
        if (data.max_cash_in_hand_allowed) body.append('max_cash_in_hand_allowed', String(data.max_cash_in_hand_allowed))
        if (data.cash_in_hand)             body.append('cash_in_hand', String(data.cash_in_hand))
        if (data.vehicle_number)           body.append('vehicle_number', data.vehicle_number)
        body.append('is_pickup_enabled', data.is_pickup_enabled ? '1' : '0')
        if (data.is_pickup_enabled && data.max_radius_in_km) body.append('max_radius_in_km', String(data.max_radius_in_km))
        if (!isSchoolAdmin && data.cash_clearance_date) body.append('cash_clearance_date', data.cash_clearance_date)
        if (isSchoolAdmin) body.append('cash_clearance_date', '2027-12-28')

        // Vendor / school
        if (isSchoolAdmin) {
          if (mySchoolId) body.append('vendor_id', String(mySchoolId))
        } else if (data.vendor_id && data.vendor_id !== 'other') {
          body.append('vendor_id', data.vendor_id)
        } else if (data.school_name) {
          body.append('school_name', data.school_name)
        }

        // Languages
        ;(data.languages ?? []).forEach((l: any, i: number) => {
          if (l.dialect_id) {
            body.append(`languages[${i}][id]`, l.dialect_id)
            body.append(`languages[${i}][fluency_level]`, l.fluency_level || 'NATIVE')
          }
        })

        // License docs
        if (data.license_front instanceof File) {
          body.append('license_file[0]', data.license_front)
          body.append('doc_side[0]', 'FRONT')
        }
        if (data.license_back instanceof File) {
          body.append('license_file[1]', data.license_back)
          body.append('doc_side[1]', 'BACK')
        }
      }

      if (isStudent) {
        if (data.gear != null) body.append('gear', String(data.gear))
      }

      // City & area — mirror old form: school-admin & state-province-preference users post state_province_id
      if (data.city_id) body.append('city_id', data.city_id)
      if (data.area_id) {
        const pref = existingUser?.data?.user_preference ?? existingUser?.user_preference
        if (isSchoolAdmin) {
          body.append('state_province_id', data.area_id)
        } else if (pref?.state_province_id && !pref?.area_id) {
          body.append('state_province_id', data.area_id)
        } else {
          body.append('area_id', data.area_id)
        }
      }
      if (data.gender)  body.append('gender', data.gender)
      if (data.vehicle_type_id) body.append('vehicle_type_id', data.vehicle_type_id)

      // Active status (old only sends this on edit, but harmless on create)
      body.append('is_active', data.is_active ? '1' : '0')

      // Collector / Assistant — city_assigned
      if (isCollector || isAssistant) {
        ;(data.city_assigned ?? []).forEach(c => { if (c) body.append('city_assigned[]', c) })
      }

      // Assistant extras
      if (isAssistant) {
        if (data.user_gender) body.append('user_gender', data.user_gender)
        if (data.assistant_access) body.append('assistant_access', data.assistant_access)
        ;(data.school_ids ?? []).forEach(s => { if (s) body.append('school_ids[]', s) })

        if (data.assistant_id_front instanceof File) {
          body.append('assistant_id_proof[0]', data.assistant_id_front)
          body.append('assistant_id_side[0]', 'FRONT')
        }
        if (data.assistant_id_back instanceof File) {
          body.append('assistant_id_proof[1]', data.assistant_id_back)
          body.append('assistant_id_side[1]', 'BACK')
        }
        if (data.assistant_id_expiry) {
          body.append('assistant_id_expiry', data.assistant_id_expiry)
          body.append('assistant_id_exipry', data.assistant_id_expiry) // legacy key kept for backend compatibility
        }
      }

      // Admin roles
      if (isAdmin) {
        ;(data.roles ?? []).forEach(r => { if (r) body.append('roles[]', r) })
      }

      let response: any
      if (isEdit) {
        // Edit: school-admin trainers go through createTrainer (with trainer_id); everyone else updateUser (with user_id)
        if (isSchoolAdmin) {
          body.append('trainer_id', String(id))
          response = await userService.createTrainer(body as any)
        } else {
          body.append('user_id', String(id))
          response = await userService.update(body as any)
        }
      } else if (isSchoolAdmin) {
        // vendor_id was already appended from mySchoolId in the isTrainer block above.
        response = await userService.createTrainer(body as any)
      } else {
        response = await userService.create(body as any)
      }

      setSuccessMsg(response?.message ?? (isEdit ? t('userCreate.successUpdated') : t('userCreate.successCreated')))
      const dest = isSchoolAdmin ? '/school/trainers-list' : '/dashboard/user/list'

      // New trainer just created → let the admin add an address for them via the
      // shared AddressModal, which needs the freshly created user id.
      let newTrainerId: number | null = null
      if (!isEdit && isTrainer) {
        // create-trainer response shapes vary; probe the common id locations.
        const pickId = (o: any): number | null => {
          if (!o || typeof o !== 'object') return null
          const v = o.user_id ?? o.id ?? o.user?.id ?? o.trainer?.user_id ?? o.trainer?.id
          return v != null && !Number.isNaN(Number(v)) ? Number(v) : null
        }
        newTrainerId = pickId(response?.data?.data) ?? pickId(response?.data) ?? pickId(response)

        // Fallback: some responses return only a message. Look the new trainer up
        // by the email/phone we just submitted so we can still attach an address.
        if (!newTrainerId && (data.email || data.phone)) {
          try {
            const term = String(data.email || data.phone)
            const res: any = isSchoolAdmin
              ? await schoolService.schoolTrainers({ search: term, limit: 20 })
              : await userService.list({ user_types: 'TRAINER', search: term, limit: 20 })
            const list: any[] = res?.data ?? []
            const match = list.find((r: any) => {
              const u = r.user ?? r
              return (data.email && u.email === data.email) ||
                     (data.phone && String(u.phone) === String(data.phone))
            }) ?? list[0]
            const mu = match?.user ?? match
            if (mu?.id != null) newTrainerId = Number(mu.id)
          } catch { /* ignore — just skip the address step */ }
        }
      }

      // Persist the inline address, if one was entered, against the new trainer.
      if (newTrainerId && addressForm.street?.trim()) {
        try {
          await userService.saveAddress({
            user_id:           newTrainerId,
            label:             addressForm.label,
            building_name:     addressForm.building_name,
            plot_number:       addressForm.plot_number,
            street:            addressForm.street,
            address:           addressForm.street,
            landmark:          addressForm.landmark,
            neighbourhood:     addressForm.neighbourhood,
            phone_number:      addressForm.phone_number,
            country_code:      addressForm.country_code,
            city_id:           addressForm.city_id || undefined,
            state_province_id: addressForm.state_province_id || undefined,
            postal_code:       addressForm.postal_code,
            additional_notes:  addressForm.additional_notes,
            latitude:          addressPos.lat,
            longitude:         addressPos.lng,
            is_default:        addressForm.is_default ? 1 : 0,
          })
        } catch { /* trainer is already created — don't block navigation on the address */ }
      }

      // Give the new trainer the school roles that were ticked.
      if (isSchoolAdmin && !isEdit && schoolRoleIds.length) {
        try {
          if (!newTrainerId) throw new Error('Could not find the new user to assign roles to.')
          await mapSchoolRoleUsers.mutateAsync({ role_id: schoolRoleIds, user_id: [newTrainerId] })
        } catch (e) {
          setSuccessMsg(`${response?.message ?? t('userCreate.successCreated')} — but roles were not assigned: ${getApiError(e)}. Assign them from Roles & Permissions.`)
        }
      }

      setTimeout(() => navigate(isEdit && !isSchoolAdmin ? `/dashboard/user/${id}` : dest), 1200)
    } catch (err: any) {
      const apiErr = err?.response?.data
      if (apiErr?.errors && typeof apiErr.errors === 'object') {
        const msgs = Object.values(apiErr.errors).flat().join(', ')
        setErrorMsg(msgs)
      } else {
        setErrorMsg(getApiError(err))
      }
    } finally {
      setSubmitting(false)
    }
  })

  // Filter user types by role. The sharjah@gov.ae account must not be able to
  // create admins, so the Admin tab is hidden for it.
  const filteredTypes = isSchoolAdmin
    ? USER_TYPES.filter(ut => ut.value === 'TRAINER')
    : USER_TYPES.filter(ut =>
        ut.value !== 'SYSTEM_ADMIN' && !(ut.value === 'ADMIN' && cannotCreateAdmin))

  return (
    <div className="space-y-5 pb-10">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
            <button type="button" onClick={() => navigate(inSchool ? '/school/overview' : '/dashboard')} className="hover:text-brand-500 hover:underline">{t('userCreate.breadcrumbDashboard')}</button><span>/</span>
            <button type="button" onClick={() => navigate(inSchool ? '/school/trainers-list' : '/dashboard/user/list')} className="hover:text-brand-500 hover:underline">{inSchool ? t('userCreate.breadcrumbTrainers') : t('userCreate.breadcrumbUsers')}</button><span>/</span>
            <span className="text-brand-500 font-medium">{isEdit ? t('userCreate.breadcrumbEditUser') : t('userCreate.breadcrumbCreateNew')}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">
            {isEdit ? t('userCreate.titleEditUser') : isSchoolAdmin ? t('userCreate.titleAddTrainer') : t('userCreate.titleCreateUser')}
          </h1>
        </div>
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 font-medium">
          <ChevronLeft size={15}/> {t('userCreate.back')}
        </button>
      </div>

      {/* ── Alerts ── */}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 font-medium">
          ✓ {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          {errorMsg}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-5">

        {/* ── User Type ── */}
        {!isSchoolAdmin && (
          <SectionCard title={t('userCreate.sectionUserType')}>
            <div className="flex flex-wrap gap-2">
              {filteredTypes.map(ut => (
                <button key={ut.value} type="button"
                  onClick={() => setValue('user_type', ut.value)}
                  className={cn(
                    'px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all',
                    watchedType === ut.value
                      ? 'text-white border-transparent'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  )}
                  style={watchedType === ut.value ? { background: '#f68b2c', borderColor: '#f68b2c' } : {}}>
                  {t(ut.labelKey)}
                </button>
              ))}
            </div>
          </SectionCard>
        )}

        {/* ── Profile Photo (skipped for Assistants for now — see onSubmit) ── */}
        {!isAssistant && (
        <SectionCard title={t('userCreate.sectionProfilePhoto')}>
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden flex-shrink-0">
              {photoPreview ? (
                <img src={photoPreview} alt={t('userCreate.avatarAlt')} className="w-full h-full object-cover"/>
              ) : (
                <Upload size={20} className="text-gray-400"/>
              )}
            </div>
            <div>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                <Upload size={14}/> {t('userCreate.uploadPhoto')}
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoDrop}/>
              </label>
              <p className="text-xs text-gray-400 mt-1">{t('userCreate.photoHint')}</p>
              {photoPreview && (
                <button type="button" onClick={() => { setPhotoPreview(null); setPhotoFile(null) }}
                  className="mt-1 text-xs text-red-500 hover:underline flex items-center gap-1">
                  <X size={11}/> {t('userCreate.remove')}
                </button>
              )}
            </div>
          </div>
        </SectionCard>
        )}

        {/* ── Basic Info ── */}
        <SectionCard title={t('userCreate.sectionBasicInfo')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label required>{t('userCreate.fullName')}</Label>
              <Input {...register('name', { required: t('userCreate.nameRequired') })} placeholder={t('userCreate.fullNamePlaceholder')} error={errors.name}/>
              <FieldError msg={errors.name?.message}/>
            </div>

            {isTrainer && (
              <>
                <div>
                  <Label>{t('userCreate.nameArabic')}</Label>
                  <Input {...register('name_ar')} placeholder={t('userCreate.nameArabicPlaceholder')} dir="rtl"/>
                </div>
                <div>
                  <Label>{t('userCreate.nameUrdu')}</Label>
                  <Input {...register('name_ur')} placeholder={t('userCreate.nameUrduPlaceholder')}/>
                </div>
              </>
            )}

            <div>
              <Label required>{t('userCreate.email')}</Label>
              <Input {...register('email', {
                required: t('userCreate.emailRequired'),
                pattern: { value: /^[^@]+@[^@]+\.[^@]+$/, message: t('userCreate.emailInvalid') }
              })} type="email" placeholder={t('userCreate.emailPlaceholder')} error={errors.email}/>
              <FieldError msg={errors.email?.message}/>
            </div>

            <div>
              <Label>{t('userCreate.password')}</Label>
              <div className="relative">
                <Input {...register('password')} type={showPassword ? 'text' : 'password'} placeholder="••••••••"/>
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff size={15}/> : <Eye size={15}/>}
                </button>
              </div>
            </div>

            <div>
              <Label>{t('userCreate.phoneNumber')}</Label>
              <div className="flex gap-2">
                <span className="inline-flex items-center px-3 py-2 border border-e-0 border-gray-300 rounded-s-lg bg-gray-50 text-sm text-gray-600">+971</span>
                <Input {...register('phone', {
                  pattern: { value: /^5\d{0,8}$/, message: t('userCreate.phoneInvalid') }
                })} placeholder={t('userCreate.phonePlaceholder')} className="rounded-s-none" error={errors.phone}/>
              </div>
              <FieldError msg={errors.phone?.message}/>
            </div>

            <div>
              <Label>{t('userCreate.dob')}</Label>
              <Input {...register('dob')} type="date"/>
            </div>

            <div className="flex items-center gap-3 pt-6">
              <Controller name="is_active" control={control} render={({ field }) => (
                <button type="button" role="switch" aria-checked={!!field.value}
                  onClick={() => field.onChange(!field.value)}
                  className={cn('relative inline-flex h-6 w-11 items-center rounded-full transition-colors', field.value ? 'bg-brand-500' : 'bg-gray-300')}>
                  <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white transition-transform', field.value ? 'translate-x-6' : 'translate-x-1')}/>
                </button>
              )}/>
              <span className="text-sm font-medium text-gray-700">{t('userCreate.activeAccount')}</span>
            </div>

            {isAssistant && (
              <>
                <div>
                  <Label>{t('userCreate.gender')}</Label>
                  <Select {...register('user_gender')}>
                    <option value="">{t('userCreate.selectGender')}</option>
                    {genders.map((g: any) => <option key={g.value} value={g.value}>{g.name}</option>)}
                  </Select>
                </div>
                <div>
                  <Label required>{t('userCreate.assistantAccess')}</Label>
                  <Select {...register('assistant_access', { required: isAssistant })} error={errors.assistant_access}>
                    <option value="">{t('userCreate.selectAccessType')}</option>
                    {assistantAccess.map((a: any) => <option key={a.value} value={a.value}>{a.name}</option>)}
                  </Select>
                  <FieldError msg={errors.assistant_access?.message}/>
                </div>
              </>
            )}

            {isAdmin && (
              <div className="sm:col-span-2">
                <Label>{t('userCreate.roles')}</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  <Controller name="roles" control={control} render={({ field }) => (
                    <>
                      {roles.map((r: any) => {
                        const checked = (field.value ?? []).includes(String(r.id))
                        return (
                          <label key={r.id} className={cn(
                            'flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-sm transition-all',
                            checked ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          )}>
                            <input type="checkbox" className="hidden" checked={checked}
                              onChange={() => {
                                const id = String(r.id)
                                const cur = field.value ?? []
                                field.onChange(cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id])
                              }}
                            />
                            {r.name}
                          </label>
                        )
                      })}
                    </>
                  )}/>
                </div>
              </div>
            )}
          </div>
        </SectionCard>

        {/* ── Preferences (Trainer / Student) ── */}
        {(isTrainer || isStudent) && (
          <SectionCard title={t('userCreate.sectionPreferences')}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>{t('userCreate.city')}</Label>
                <Select {...register('city_id')}>
                  <option value="">{t('userCreate.selectCity')}</option>
                  {cities.map((c: any) => {
                    const tr = c.city_translations?.find((ct: any) => ct.locale?.toLowerCase() === 'en') ?? c.city_translations?.[0]
                    return <option key={c.id ?? tr?.city_id} value={c.id ?? tr?.city_id}>{tr?.name ?? t('userCreate.unknown')}</option>
                  })}
                </Select>
              </div>

              <div>
                <Label>{t('userCreate.areaDistrict')}</Label>
                <Select {...register('area_id')} disabled={!watchedCity}>
                  <option value="">{watchedCity ? t('userCreate.selectArea') : t('userCreate.selectCityFirst')}</option>
                  {areas.map((a: any) => (
                    <option key={a.id} value={a.id}>{areaName(a) ?? t('userCreate.unknown')}</option>
                  ))}
                </Select>
              </div>

              <div>
                <Label required={isTrainer}>{t('userCreate.gear')}</Label>
                <Select {...register('gear', { required: isTrainer ? t('userCreate.gearRequired') : false })} error={errors.gear}>
                  <option value="">{t('userCreate.selectGear')}</option>
                  {gears.map((g: any) => <option key={g.value} value={g.value}>{g.name}</option>)}
                </Select>
                <FieldError msg={errors.gear?.message}/>
              </div>

              <div>
                <Label>{t('userCreate.genderPreference')}</Label>
                <Select {...register('gender')}>
                  <option value="">{t('userCreate.selectGender')}</option>
                  {genders.map((g: any) => <option key={g.value} value={g.value}>{g.name}</option>)}
                </Select>
              </div>

              <div>
                <Label>{t('userCreate.vehicleCategory')}</Label>
                <Select {...register('vehicle_type_id')}>
                  <option value="">{t('userCreate.selectCategory')}</option>
                  {categories.map((c: any) => {
                    const name = c.category_translations?.map((ct: any) => ct.name).join(' - ')
                    return <option key={c.id} value={c.id}>{name}</option>
                  })}
                </Select>
              </div>
            </div>
          </SectionCard>
        )}

        {/* ── Trainer-specific ── */}
        {isTrainer && (
          <>
            <SectionCard title={t('userCreate.sectionSchoolCommission')}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {isSchoolAdmin ? (
                  <>
                    <div>
                      <Label>{t('userCreate.school')}</Label>
                      <Input disabled value={mySchoolName} />
                    </div>
                    <div>
                      <Label>{t('userCreate.schoolCommission')}</Label>
                      <Input {...register('vendor_commission_in_percentage')} type="number" min={0} max={100} placeholder="0"/>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label>{t('userCreate.school')}</Label>
                      <Select {...register('vendor_id')} onChange={(e: any) => { setValue('vendor_id', e.target.value); setSchoolSearch('') }}>
                        <option value="">{t('userCreate.selectSchool')}</option>
                        {schools.map((s: any) => {
                          const tr = s.vendor_translations?.find((st: any) => st.locale?.toLowerCase() === 'en') ?? s.vendor_translations?.[0]
                          return <option key={s.id} value={s.id}>{tr?.name ?? t('userCreate.unknown')}{s.email ? ` - ${s.email}` : ''}</option>
                        })}
                        <option value="other">{t('userCreate.other')}</option>
                      </Select>
                    </div>

                    {showOtherSchool && (
                      <div>
                        <Label>{t('userCreate.schoolNameIfNotListed')}</Label>
                        <Input {...register('school_name')} placeholder={t('userCreate.enterSchoolName')}/>
                      </div>
                    )}

                    <div>
                      <Label>{t('userCreate.schoolCommission')}</Label>
                      <Input {...register('vendor_commission_in_percentage')} type="number" min={0} max={100} placeholder="0"/>
                    </div>
                  </>
                )}

                <div>
                  <Label>{t('userCreate.certificateCommission')}</Label>
                  <Input {...register('certificate_commission_in_percentage')} type="number"
                    disabled={isSchoolAdmin} placeholder="0"/>
                </div>

                <div>
                  <Label>{t('userCreate.certificateExpiryDate')}</Label>
                  <Input {...register('certificate_expiry_date')} type="date"/>
                </div>

                {!isSchoolAdmin && (
                  <div>
                    <Label>{t('userCreate.cashClearanceDate')}</Label>
                    <Input {...register('cash_clearance_date')} type="date"/>
                  </div>
                )}

                {!isSchoolAdmin && (
                  <>
                    <div>
                      <Label>{t('userCreate.maxCashInHandAllowed')}</Label>
                      <Input {...register('max_cash_in_hand_allowed')} type="number" placeholder="0"/>
                    </div>
                    <div>
                      <Label>{t('userCreate.cashInHand')}</Label>
                      <Input {...register('cash_in_hand')} type="number" placeholder="0"/>
                    </div>
                  </>
                )}

                <div>
                  <Label>{t('userCreate.vehicleNumber')}</Label>
                  <Input {...register('vehicle_number')} placeholder={t('userCreate.platePlaceholder')}/>
                </div>
              </div>

              {/* Pickup toggle */}
              <div className="flex items-center gap-3 mt-2">
                <Controller name="is_pickup_enabled" control={control} render={({ field }) => (
                  <button type="button" onClick={() => field.onChange(!field.value)}
                    className={cn('relative w-11 h-6 rounded-full transition-colors', field.value ? 'bg-brand-500' : 'bg-gray-300')}>
                    <span className={cn('absolute top-0.5 start-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform', field.value && 'translate-x-5')}/>
                  </button>
                )}/>
                <span className="text-sm text-gray-700 font-medium">{t('userCreate.pickupEnabled')}</span>
              </div>

              {watchedPickup && (
                <div className="mt-2 max-w-xs">
                  <Label>{t('userCreate.maxRadius')}</Label>
                  <Input {...register('max_radius_in_km')} type="number" min={1} max={70} placeholder={t('userCreate.radiusPlaceholder')}/>
                </div>
              )}
            </SectionCard>

            {/* Bio */}
            <SectionCard title={t('userCreate.sectionBio')}>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>{t('userCreate.aboutEnglish')}</Label>
                  <textarea {...register('bio')} rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
                    placeholder={t('userCreate.aboutPlaceholder')}/>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{t('userCreate.aboutArabic')}</Label>
                    <textarea {...register('bio_ar')} rows={2} dir="rtl"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"/>
                  </div>
                  <div>
                    <Label>{t('userCreate.aboutUrdu')}</Label>
                    <textarea {...register('bio_ur')} rows={2}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"/>
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Languages */}
            <SectionCard title={t('userCreate.sectionLanguages')}>
              <Controller name="languages" control={control} render={({ field }) => (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {dialects.map((d: any) => {
                    const isSelected = (field.value ?? []).some((l: any) => l.dialect_id === String(d.id))
                    return (
                      <label key={d.id} className={cn(
                        'flex items-center gap-2 px-3 py-2 rounded-xl border-2 cursor-pointer text-sm transition-all',
                        isSelected ? 'border-brand-500 bg-brand-50 text-brand-800 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      )}>
                        <input type="checkbox" className="hidden" checked={isSelected}
                          onChange={() => {
                            const cur = field.value ?? []
                            if (isSelected) {
                              field.onChange(cur.filter((l: any) => l.dialect_id !== String(d.id)))
                            } else {
                              field.onChange([...cur, { dialect_id: String(d.id), fluency_level: 'NATIVE' }])
                            }
                          }}/>
                        {isSelected && <span className="text-brand-600">✓</span>}
                        {d.dialect_name ?? d.name ?? 'Unknown'}
                      </label>
                    )
                  })}
                </div>
              )}/>
            </SectionCard>

            {/* License Docs */}
            <SectionCard title={t('userCreate.sectionLicenseDocs')}>
              <div className="grid sm:grid-cols-2 gap-5">
                {(['front', 'back'] as const).map(side => {
                  const fieldName = side === 'front' ? 'license_front' : 'license_back'
                  return (
                    <div key={side} className="border border-dashed border-gray-300 rounded-xl p-4 space-y-2">
                      <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{t(side === 'front' ? 'userCreate.frontSide' : 'userCreate.backSide')}</p>
                      <Controller name={fieldName as any} control={control} render={({ field }) => (
                        <>
                          <label className="cursor-pointer flex flex-col items-center gap-2 py-4 text-gray-400 hover:text-gray-600">
                            <Upload size={20}/>
                            <span className="text-xs">{t('userCreate.clickToUpload')}</span>
                            <input type="file" accept="image/*,application/pdf" className="hidden"
                              onChange={e => field.onChange(e.target.files?.[0] ?? null)}/>
                          </label>
                          {field.value instanceof File && (
                            <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
                              <span className="text-xs text-green-700 truncate flex-1">{(field.value as File).name}</span>
                              <button type="button" onClick={() => field.onChange(null)}>
                                <X size={14} className="text-red-400"/>
                              </button>
                            </div>
                          )}
                        </>
                      )}/>
                    </div>
                  )
                })}
              </div>
            </SectionCard>

            {/* ── Address (optional) — saved against the new trainer after create ── */}
            {!isEdit && (
              <SectionCard title={t('userCreate.sectionAddress')}>
                <AddressPicker
                  value={addressForm}
                  onChange={patch => setAddressForm(f => ({ ...f, ...patch }))}
                  pos={addressPos}
                  onPosChange={setAddressPos}
                  active={isTrainer}
                  hideDefaultToggle
                />
              </SectionCard>
            )}
          </>
        )}

        {/* ── Collector / Assistant — City Assigned ── */}
        {(isCollector || isAssistant) && (
          <SectionCard title={t('userCreate.sectionCityAssignment')}>
            <Controller name="city_assigned" control={control} render={({ field }) => (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {cities.map((c: any) => {
                  const tr = c.city_translations?.find((t: any) => t.locale?.toLowerCase() === 'en') ?? c.city_translations?.[0]
                  const id = String(c.id ?? tr?.city_id)
                  const isSelected = (field.value ?? []).includes(id)
                  return (
                    <label key={id} className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-xl border-2 cursor-pointer text-sm transition-all',
                      isSelected ? 'border-brand-500 bg-brand-50 text-brand-800 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    )}>
                      <input type="checkbox" className="hidden" checked={isSelected}
                        onChange={() => {
                          const cur = field.value ?? []
                          field.onChange(isSelected ? cur.filter(x => x !== id) : [...cur, id])
                        }}/>
                      {isSelected && <span className="text-brand-600">✓</span>}
                      {tr?.name ?? 'Unknown'}
                    </label>
                  )
                })}
              </div>
            )}/>
          </SectionCard>
        )}

        {/* ── Assistant — School Assignment ── */}
        {isAssistant && (
          <>
            <SectionCard title={t('userCreate.sectionSchoolAssignment')}>
              <Controller name="school_ids" control={control} render={({ field }) => (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {schools.map((s: any) => {
                    const tr = s.vendor_translations?.find((t: any) => t.locale?.toLowerCase() === 'en') ?? s.vendor_translations?.[0]
                    const id = String(s.id)
                    const isSelected = (field.value ?? []).includes(id)
                    return (
                      <label key={id} className={cn(
                        'flex items-center gap-2 px-3 py-2 rounded-xl border-2 cursor-pointer text-sm transition-all',
                        isSelected ? 'border-brand-500 bg-brand-50 text-brand-800 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      )}>
                        <input type="checkbox" className="hidden" checked={isSelected}
                          onChange={() => {
                            const cur = field.value ?? []
                            field.onChange(isSelected ? cur.filter(x => x !== id) : [...cur, id])
                          }}/>
                        {isSelected && <span className="text-brand-600">✓</span>}
                        <span className="truncate">{tr?.name ?? 'Unknown'}</span>
                      </label>
                    )
                  })}
                </div>
              )}/>
            </SectionCard>

            {/* Assistant ID Proof */}
            <SectionCard title={t('userCreate.sectionAssistantIdProof')}>
              <div className="grid sm:grid-cols-2 gap-5">
                {(['front', 'back'] as const).map(side => {
                  const fieldName = side === 'front' ? 'assistant_id_front' : 'assistant_id_back'
                  return (
                    <div key={side} className="border border-dashed border-gray-300 rounded-xl p-4 space-y-2">
                      <p className="text-sm font-semibold text-gray-700 uppercase">{t(side === 'front' ? 'userCreate.frontSide' : 'userCreate.backSide')}</p>
                      <Controller name={fieldName as any} control={control} render={({ field }) => (
                        <>
                          <label className="cursor-pointer flex flex-col items-center gap-2 py-4 text-gray-400 hover:text-gray-600">
                            <Upload size={20}/>
                            <span className="text-xs">{t('userCreate.clickToUpload')}</span>
                            <input type="file" accept="image/*,application/pdf" className="hidden"
                              onChange={e => field.onChange(e.target.files?.[0] ?? null)}/>
                          </label>
                          {field.value instanceof File && (
                            <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
                              <span className="text-xs text-green-700 truncate flex-1">{(field.value as File).name}</span>
                              <button type="button" onClick={() => field.onChange(null)}><X size={14} className="text-red-400"/></button>
                            </div>
                          )}
                        </>
                      )}/>
                    </div>
                  )
                })}
              </div>
              <div className="max-w-xs mt-2">
                <Label>{t('userCreate.idExpiryDate')}</Label>
                <Input {...register('assistant_id_expiry')} type="date"/>
              </div>
            </SectionCard>
          </>
        )}

        {/* ── School roles (school admin creating a user) ── */}
        {isSchoolAdmin && !isEdit && schoolRoles.length > 0 && (
          <SectionCard title="Roles">
            <p className="text-xs text-gray-400 mb-2">Choose what this user is allowed to do in your school. You can change it later in Roles &amp; Permissions.</p>
            <div className="flex flex-wrap gap-2">
              {schoolRoles.map(r => {
                const checked = schoolRoleIds.includes(r.id)
                return (
                  <label key={r.id} title={r.description} className={cn(
                    'flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-sm transition-all',
                    checked ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  )}>
                    <input type="checkbox" className="hidden" checked={checked}
                      onChange={() => setSchoolRoleIds(cur => checked ? cur.filter(x => x !== r.id) : [...cur, r.id])} />
                    {r.name}
                  </label>
                )
              })}
            </div>
          </SectionCard>
        )}

        {/* ── Submit ── */}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => navigate(-1)}
            className="px-5 py-2.5 text-sm font-semibold border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={submitting}
            className="px-6 py-2.5 text-sm font-bold rounded-xl text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ background: '#f68b2c' }}>
            {submitting ? (isEdit ? t('userCreate.saving') : t('userCreate.creating')) : isEdit ? t('userCreate.saveChanges') : isSchoolAdmin ? t('userCreate.createTrainer') : t('userCreate.createUser')}
          </button>
        </div>
      </form>
    </div>
  )
}
